// EPIC D PR1 — Service time tracking
// Pattern projet : INSERT/UPDATE/DELETE via RPC SECURITY DEFINER, SELECT direct.
import { supabase } from '../../lib/supabaseClient';
import type { Tables } from '../../types/database.types';

export type TimeEntry = Tables<'time_entries'>;

export interface TimeEntryFilters {
  dossier_id?: string;
  collaborator_id?: string;
  from?: string; // ISO date
  to?: string;   // ISO date
  invoiced?: boolean | null; // true = facturées, false = non facturées, null = tout
}

export interface CreateTimeEntryInput {
  dossier_id: string;
  started_at: string;        // ISO timestamp
  duration_minutes: number;
  task_id?: string | null;
  is_billable?: boolean;
  billing_code?: string | null;
  description?: string | null;
  ended_at?: string | null;
}

export interface UpdateTimeEntryInput {
  dossier_id?: string;
  task_id?: string | null;
  started_at?: string;
  duration_minutes?: number;
  is_billable?: boolean;
  billing_code?: string | null;
  description?: string | null;
}

// ── SELECT ──────────────────────────────────────────────────────────────

export async function listTimeEntries(filters: TimeEntryFilters = {}): Promise<TimeEntry[]> {
  let q = supabase.from('time_entries').select('*');
  if (filters.dossier_id) q = q.eq('dossier_id', filters.dossier_id);
  if (filters.collaborator_id) q = q.eq('collaborator_id', filters.collaborator_id);
  if (filters.from) q = q.gte('started_at', filters.from);
  if (filters.to) q = q.lte('started_at', filters.to);
  if (filters.invoiced === true) q = q.not('invoiced_in_id', 'is', null);
  if (filters.invoiced === false) q = q.is('invoiced_in_id', null);
  q = q.order('started_at', { ascending: false });
  const { data, error } = await q;
  if (error) throw new Error(`Failed to list time entries: ${error.message}`);
  return data ?? [];
}

// ── MUTATIONS ──────────────────────────────────────────────────────────

export async function createTimeEntry(input: CreateTimeEntryInput): Promise<TimeEntry> {
  const { data, error } = await supabase.rpc('create_time_entry', {
    p_dossier_id: input.dossier_id,
    p_started_at: input.started_at,
    p_duration_minutes: input.duration_minutes,
    p_task_id: input.task_id ?? undefined,
    p_is_billable: input.is_billable ?? true,
    p_billing_code: input.billing_code ?? undefined,
    p_description: input.description ?? undefined,
    p_ended_at: input.ended_at ?? undefined,
  });
  if (error) throw new Error(`Failed to create time entry: ${error.message}`);
  return data as TimeEntry;
}

export async function updateTimeEntry(id: string, input: UpdateTimeEntryInput): Promise<TimeEntry> {
  const { data, error } = await supabase.rpc('update_time_entry', {
    p_id: id,
    p_dossier_id: input.dossier_id ?? undefined,
    p_task_id: input.task_id ?? undefined,
    p_started_at: input.started_at ?? undefined,
    p_duration_minutes: input.duration_minutes ?? undefined,
    p_is_billable: input.is_billable ?? undefined,
    p_billing_code: input.billing_code ?? undefined,
    p_description: input.description ?? undefined,
  });
  if (error) throw new Error(`Failed to update time entry: ${error.message}`);
  return data as TimeEntry;
}

export async function deleteTimeEntry(id: string): Promise<void> {
  const { error } = await supabase.rpc('delete_time_entry', { p_id: id });
  if (error) throw new Error(`Failed to delete time entry: ${error.message}`);
}

export async function listBillingCodes(): Promise<{ code: string; usage_count: number }[]> {
  const { data, error } = await supabase.rpc('list_billing_codes');
  if (error) throw new Error(`Failed to list billing codes: ${error.message}`);
  return (data ?? []) as { code: string; usage_count: number }[];
}

// ── HELPERS ────────────────────────────────────────────────────────────

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${String(m).padStart(2, '0')}`;
}

/**
 * Parse "1h30", "1:30", "90", "90min", "1.5h" → minutes.
 * Returns null if invalid.
 */
export function parseDuration(input: string): number | null {
  const trimmed = input.trim().toLowerCase().replace(/\s+/g, '');
  if (!trimmed) return null;

  // Format hh:mm
  const colonMatch = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (colonMatch) {
    const h = Number(colonMatch[1]);
    const m = Number(colonMatch[2]);
    if (m >= 60) return null;
    return h * 60 + m;
  }

  // Format 1h30, 2h
  const hMinMatch = trimmed.match(/^(\d+)h(\d{0,2})$/);
  if (hMinMatch) {
    const h = Number(hMinMatch[1]);
    const m = hMinMatch[2] ? Number(hMinMatch[2]) : 0;
    if (m >= 60) return null;
    return h * 60 + m;
  }

  // Format 1.5h, 0.25h
  const decimalH = trimmed.match(/^(\d+(?:\.\d+)?)h$/);
  if (decimalH) {
    const h = Number(decimalH[1]);
    return Math.round(h * 60);
  }

  // Format 90min
  const minMatch = trimmed.match(/^(\d+)min$/);
  if (minMatch) return Number(minMatch[1]);

  // Nombre brut → minutes
  if (/^\d+$/.test(trimmed)) return Number(trimmed);

  return null;
}

export function computeAmount(entry: TimeEntry): number | null {
  if (!entry.is_billable) return null;
  if (!entry.hourly_rate_snapshot) return null;
  return Math.round((entry.duration_minutes / 60) * Number(entry.hourly_rate_snapshot) * 100) / 100;
}
